import { useState } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import {
    MapPinIcon,
    ClockIcon,
    DollarSignIcon,
    BookmarkIcon,
    StarIcon,
    Building,
} from 'lucide-react'
import { useAlert } from '../../../../constants/AlertProvider';
import { formatRelativeDate } from '../../../../utils/formatRelativeDate';
import { formatSalary } from '../../../../utils/formatSalary';
import { JobExpand } from './JobExpand'

export function JobCard({ job, company, onJobUpdated }) {
    const tags = [];
    const [isJobExpanded, setIsJobExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const showAlert = useAlert();

    if (job.is_remote) tags.push('Remote');
    else tags.push('On-site');
    if (job.job_type === 'full-time') tags.push('Full-time');
    else if (job.job_type === 'intern') tags.push('Internship');

    function getInitials(name) {
        if (!name) return '';
        return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    }

    const bgColors = [
        "bg-purple-200",
        "bg-blue-200",
        "bg-green-200"
    ];

    // Function to pick a color based on company name (repeatable)
    function getBgColor(name) {
        if (!name) return bgColors[0];
        // Simple hash: sum char codes and mod by colors length
        const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
        return bgColors[hash % bgColors.length];
    }

    const toggleJobExpand = () => {
        setIsJobExpanded((prev) => !prev);
    }

    const toggleSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const res = await axios.post(
                '/api/match-results/save-job/',
                { job: job.id, is_saved: !job.isSaved },
                { withCredentials: true, headers: { 'X-CSRFToken': Cookies.get('csrftoken') } }
            );
            onJobUpdated?.(job.id, { matchId: res.data.id, isSaved: res.data.is_saved });
        } catch (error) {
            showAlert('Failed to update saved jobs', 'error');
            console.error('Failed to save job', error);
        } finally {
            setIsSaving(false);
        }
    };

    const companyName = company?.name || '';
    const companyLogo = company?.logo || null;
    const logoBgColor = getBgColor(companyName);

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
                <div className="flex items-start">
                    <div className={`h-12 w-12 flex-shrink-0 rounded flex items-center justify-center overflow-hidden mr-4 ${!companyLogo ? logoBgColor : ''}`}>
                        {companyLogo ? (
                            <img
                                src={companyLogo}
                                alt={`${companyName} logo`}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <span className="text-xl font-bold text-gray-600 dark:text-gray-300">
                                {getInitials(companyName)}
                            </span>
                        )}
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">{job.title}</h3>
                        <div className="mt-2 flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 gap-y-1">
                            <div className='flex items-center justify-center gap-x-1'>
                                <Building className='text-gray-600 dark:text-gray-400 w-4 h-4' />
                                <p className="text-gray-600 dark:text-gray-400 font-medium">{companyName}</p>
                            </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 gap-y-1">
                            <div className="flex items-center mr-4 gap-x-1">
                                <MapPinIcon size={14} className="mr-1" />
                                <span>{job.city}, {job.state}, {job.country}</span>
                            </div>
                            <div className="flex items-center">
                                <DollarSignIcon size={14} className="mr-1" />
                                <span>{formatSalary(job.salary_min)} - {formatSalary(job.salary_max)}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col items-end">
                    {job.matchScore != null && (
                        <div
                            className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${job.matchScore >= 90 ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' : job.matchScore >= 70 ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'}`}
                        >
                            <StarIcon size={12} className="mr-1" />
                            {job.matchScore}% Match
                        </div>
                    )}
                    <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Posted {formatRelativeDate(job.created_at)}
                    </span>
                </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
                <div className="flex items-center">
                    <button onClick={toggleJobExpand} type='button' className='w-14 h-8 bg-blue-200 hover:bg-blue-300 font-semidbold text-md text-blue-600 rounded-md'>
                        {isJobExpanded ? "Hide" : "View"}
                    </button>
                    <button
                        type="button"
                        onClick={toggleSave}
                        disabled={isSaving}
                        aria-label={job.isSaved ? 'Unsave job' : 'Save job'}
                        className={`p-2 rounded-full disabled:opacity-50 ${job.isSaved ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    >
                        <BookmarkIcon
                            size={18}
                            fill={job.isSaved ? 'currentColor' : 'none'}
                        />
                    </button>
                </div>
            </div>
            {isJobExpanded &&
                <JobExpand className="rounded-md bg-amber-200" job={job} company={company} onClose={toggleJobExpand} />
            }
        </div>
    )
}
